import React from 'react'
import {
    returnCardInformation,
    raiseSelectItemEvent,
} from '@news-mono/web-common'
import { IconNextGenPerthNowComment } from '../../../../../icons/IconNextGenPerthNowComment/IconNextGenPerthNowComment'
import {
    StyledFooter,
    StyledCommentAnchor,
    StyledAuthor,
    StyledAuthorCommentGroup,
} from './PNArticleCardFooter.styled'
import { PNArticleCardProps } from '../PNArticleCard'
import { colorsPN } from '@news-mono/design-tokens'
import { CoralCommentCount } from '../../../../../coral/CoralCommentCount'

/**
 * A React functional component that renders the footer of a news article card.
 *
 * @param props - The component's props.
 * @param props.item - The data item containing the article information.
 * @param props.cardType - The type of the card.
 *
 * @returns A React element representing the footer of the news article card.
 */

export const PNArticleCardFooter: React.FC<
    PNArticleCardProps & { expandedGridSetup?: boolean }
> = (props) => {
    const {
        item,
        onEvent,
        cardType,
        fontScale = 'M',
        cardNumber,
        showAuthor = false,
        isTimestamped: timeStamped = false,
        expandedGridSetup = false,
        showBreakingStyling,
        isTheNightlyCuration = false,
    } = props

    if (item.loaded) {
        const { result } = item
        const { link, allowCommenting = false, _self, byline } = result

        const isBreakingStyle =
            showBreakingStyling && result.classification?.label === 'breaking'

        const cardInfo = returnCardInformation(result)

        // Helper that fires the select_item list event
        const linkClicked = (originator: string) => {
            raiseSelectItemEvent(
                onEvent,
                originator,
                cardInfo,
                result,
                cardNumber,
            )
        }

        return (
            <StyledFooter
                role="contentinfo"
                data-card-type={cardType}
                data-font-scale={fontScale}
                isTimestamped={timeStamped}
                expandedGridSetup={expandedGridSetup}
            >
                <StyledAuthorCommentGroup>
                    {showAuthor && byline && (
                        <StyledAuthor
                            hasDivider={allowCommenting}
                            isBreakingStyle={isBreakingStyle}
                        >
                            {byline}
                        </StyledAuthor>
                    )}
                    {allowCommenting && (
                        <StyledCommentAnchor
                            to={`${
                                isTheNightlyCuration ? _self : link
                            }#coral_thread?utm_source=perthnow&utm_medium=homepage-curation&utm_campaign=thenightly-curation`}
                            aria-label="Comments count"
                            tabIndex={0}
                            onClick={() =>
                                linkClicked('pn-article-card-comments')
                            }
                            isBreakingStyle={isBreakingStyle}
                        >
                            <span>
                                {_self && (
                                    <CoralCommentCount articleURL={_self} />
                                )}
                                <IconNextGenPerthNowComment
                                    color={
                                        isBreakingStyle
                                            ? colorsPN.fill.base
                                            : undefined
                                    }
                                />
                            </span>
                        </StyledCommentAnchor>
                    )}
                </StyledAuthorCommentGroup>
            </StyledFooter>
        )
    }

    // Return null if the item is not loaded
    return null
}
